import React, { useState, useEffect } from "react";
import Sidebar from "../components/Sidebar";

interface Product {
  id: string;
  name: string;
  price: number;
  stock: number;
  image?: string;
}

interface CartItem extends Product {
  quantity: number;
}

const POS: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [cart, setCart] = useState<CartItem[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");

  useEffect(() => {
    setProducts([
      { id: "PRD-001", name: "หูฟังไร้สาย", price: 2500, stock: 50, image: "🎧" },
      { id: "PRD-002", name: "สมาร์ทวอทช์", price: 3200, stock: 30, image: "⌚" },
      { id: "PRD-003", name: "เสื้อยืด", price: 350, stock: 100, image: "👕" },
      { id: "PRD-004", name: "รองเท้ากีฬา", price: 1200, stock: 25, image: "👟" },
      { id: "PRD-005", name: "กระเป๋าผ้า", price: 450, stock: 40, image: "🎒" },
      { id: "PRD-006", name: "แก้วน้ำ", price: 150, stock: 80, image: "🥤" },
      { id: "PRD-007", name: "หนังสือ", price: 250, stock: 60, image: "📚" },
      { id: "PRD-008", name: "ปากกา", price: 50, stock: 200, image: "🖊️" },
    ]);
  }, []);

  const filteredProducts = products.filter((product) => {
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = categoryFilter === "all" || categoryFilter === product.id.substring(4, 5);
    return matchesSearch && matchesCategory;
  });

  const addToCart = (product: Product) => {
    setCart((prevCart) => {
      const existingItem = prevCart.find((item) => item.id === product.id);
      if (existingItem) {
        return prevCart.map((item) =>
          item.id === product.id ? { ...item, quantity: item.quantity + 1 } : item
        );
      }
      return [...prevCart, { ...product, quantity: 1 }];
    });
  };

  const removeFromCart = (productId: string) => {
    setCart((prevCart) => prevCart.filter((item) => item.id !== productId));
  };

  const updateQuantity = (productId: string, quantity: number) => {
    if (quantity <= 0) {
      removeFromCart(productId);
      return;
    }
    setCart((prevCart) =>
      prevCart.map((item) => (item.id === productId ? { ...item, quantity } : item))
    );
  };

  const cartTotal = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const cartTotalItems = cart.reduce((sum, item) => sum + item.quantity, 0);

  const handleCheckout = () => {
    if (cart.length === 0) return;
    alert(`ชำระเงิน: ฿${cartTotal.toLocaleString()}\nรายการ: ${cartTotalItems} ชิ้น`);
    setCart([]);
  };

  return (
    <div className="flex min-h-screen bg-gray-100">
      <Sidebar />
      <div className="flex-1 p-8">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">จุดขาย (POS)</h1>
          <p className="text-gray-600 mb-6">ระบบจุดขายและการชำระเงิน</p>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Products Grid */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-lg shadow p-4 mb-4 border border-gray-200">
                <input
                  type="text"
                  placeholder="ค้นหาสินค้า..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                />
              </div>

              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {filteredProducts.map((product) => (
                  <div
                    key={product.id}
                    className="bg-white rounded-lg shadow p-4 border border-gray-200 hover:shadow-lg transition-shadow cursor-pointer"
                    onClick={() => addToCart(product)}
                  >
                    <div className="text-4xl mb-2 text-center">{product.image}</div>
                    <h3 className="text-sm font-medium text-gray-900 mb-1">{product.name}</h3>
                    <p className="text-lg font-bold text-blue-600">฿{product.price.toLocaleString()}</p>
                    <p className="text-xs text-gray-500">สต็อก: {product.stock}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Cart */}
            <div className="bg-white rounded-lg shadow border border-gray-200">
              <div className="p-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">ตะกร้าสินค้า</h2>
                <p className="text-sm text-gray-500">{cartTotalItems} รายการ</p>
              </div>

              <div className="p-4 space-y-3 max-h-96 overflow-y-auto">
                {cart.length === 0 ? (
                  <p className="text-center text-gray-500 py-8">ตะกร้าว่าง</p>
                ) : (
                  cart.map((item) => (
                    <div key={item.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">{item.image}</span>
                        <div>
                          <h4 className="text-sm font-medium text-gray-900">{item.name}</h4>
                          <p className="text-sm text-blue-600">฿{item.price.toLocaleString()}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => updateQuantity(item.id, item.quantity - 1)}
                          className="w-8 h-8 bg-gray-200 rounded-full hover:bg-gray-300 transition-colors"
                        >
                          -
                        </button>
                        <span className="w-8 text-center">{item.quantity}</span>
                        <button
                          onClick={() => updateQuantity(item.id, item.quantity + 1)}
                          className="w-8 h-8 bg-gray-200 rounded-full hover:bg-gray-300 transition-colors"
                        >
                          +
                        </button>
                        <button
                          onClick={() => removeFromCart(item.id)}
                          className="text-red-500 hover:text-red-700 ml-2"
                        >
                          ×
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>

              <div className="p-4 border-t border-gray-200">
                <div className="flex justify-between mb-4">
                  <span className="text-gray-600">ยอดรวม</span>
                  <span className="text-xl font-bold text-gray-900">฿{cartTotal.toLocaleString()}</span>
                </div>
                <button
                  onClick={handleCheckout}
                  disabled={cart.length === 0}
                  className={`w-full py-3 rounded-lg font-medium transition-colors ${
                    cart.length === 0
                      ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                      : "bg-blue-600 text-white hover:bg-blue-700"
                  }`}
                >
                  ชำระเงิน
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default POS;
